<template>
  <div :id="addOnId" class="addon">
    <v-layout wrap>
      <v-flex xs12 md2 px-2>
        <v-text-field
          v-model="addon.attributes.name"
          label="Name"
          placeholder="Add-On Name"
          required
        />
      </v-flex>

      <v-flex class="xs12 md3 px-2" v-if="serviceCategories.length > 0">
        <v-select
          v-model="addon.attributes.service_category_id"
          :items="serviceCategories"
          item-text="attributes.name"
          item-value="attributes.id"
          label="Service Category *"
          :error="!addon.attributes.service_category_id"
        />
      </v-flex>

      <v-flex xs12 md3 px-2>
        <v-text-field
          v-model="addon.attributes.description"
          label="Description"
          placeholder="Add-On Description"
        />
      </v-flex>

      <v-flex xs12 md2 px-2>
        <div class="checkbox-top-label">
          <label>Hide from scheduler</label>
          <v-checkbox v-model="addon.attributes.hidden"></v-checkbox>
          <help-tooltip
            tooltip="This add on will not be availale on your online scheduler (i.e. only you and your staff can add it.)"
          />
        </div>
      </v-flex>
      <v-flex xs3 md1 justify-center pa-2 class="valign-wrapper">
        <div class="btn-blank btn-flat btn-small btn-reorder-add-ons">
          <div class="fa fa-exchange rotate90" />
        </div>
        <div class="btn-blank btn-flat btn-small danger" @click="deleteAddOn">
          <div class="fa fa-trash" />
        </div>
      </v-flex>
    </v-layout>

    <v-layout wrap>
      <v-flex xs12 md3 px-2>
        <v-select
          v-model="addon.attributes.templates"
          :items="templates"
          item-text="attributes.name"
          item-value="id"
          label="Add Template"
          multiple
        />
      </v-flex>

      <v-flex v-if="hasAgreements" xs12 md3 px-2>
        <v-select
          v-model="addon.attributes.agreements"
          :items="agreements"
          item-text="attributes.name"
          item-value="id"
          label="Add Agreement"
          multiple
        />
      </v-flex>

      <v-flex xs4 md1 px-2>
        <v-text-field
          v-if="hasPayments"
          v-model="addon.attributes.amount"
          label="Add Fee"
          type="number"
          min="0.00"
          step="0.01"
          placeholder="0.00"
        />
      </v-flex>

      <v-flex xs4 md1 px-2>
        <v-text-field
          v-model="addon.attributes.duration"
          label="Add Hours"
          type="number"
          min="0.0"
          step="0.25"
          placeholder="0.0"
        />
      </v-flex>

      <v-flex xs12 md2 px-2>
        <div class="checkbox-top-label">
          <label>Allow Upsell</label>
          <v-checkbox
            v-model="addon.attributes.settings.show_as_upsell"
          ></v-checkbox>
          <help-tooltip
            tooltip="This add-on will be displayed on the Client Portal. In the future we will allow automated upsells."
          />
          <!-- <help-tooltip tooltip="This add on will be available for the client to automatically add via the Client Portal. You can control how you get notified of upsells in Settings->Client Portal" /> -->
        </div>
      </v-flex>
    </v-layout>

    <v-layout wrap>
      <v-flex v-if="companyEvents" xs12 md3 px-2 class="has-tooltip">
        <v-text-field
          v-model="eventsString"
          placeholder="Event Names (comma separated)"
          label="Default Inspection Events"
        />
        <help-tooltip
          tooltip="These events will automatically be added for you when scheduling an inspection with this add-on."
        />
      </v-flex>

      <organization-service-id-input
        classes="md4 px-2"
        :organization-service-id.sync="organizationServiceAddOnId"
        :show="canShowServiceIdInput"
      />
    </v-layout>

    <v-expansion-panel :multiple="true">
      <v-expansion-panel-content
        :id="addOnId"
        class="service-modifiers"
        expand-icon=""
      >
        <div class="service-modifiers-header" slot="header">
          <div class="inline">
            <div class="fa fa-chevron-right light" />
            <h5>Modifiers</h5>
          </div>
          <p class="light small">
            Add additional fees &amp; hours to your Add Ons when the property
            matches certain criteria like square footage or age.
          </p>
        </div>
        <v-flex class="xs12 pa-3">
          <ul>
            <li
              is="modifier-item"
              :key="modifier.id"
              :modifier="modifier"
              v-for="modifier in filteredServiceAddOnModifiers"
            />
          </ul>
          <div class="col s12 center-align">
            <div
              class="btn btn-blank btn-flat mt-3"
              @click="createServiceAddOnModifier"
            >
              <div class="fa fa-plus" />
              Modifiers
            </div>
          </div>
        </v-flex>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import HelpTooltip from '../../shared/HelpTooltip'
import ModifierItem from './ModifierItem'
import OrganizationServiceIdInput from './OrganizationServiceIdInput'
import ServiceModifier from '../../../models/ServiceModifier'

export default {
  components: {
    HelpTooltip,
    ModifierItem,
    OrganizationServiceIdInput,
  },
  computed: {
    ...mapState('services', [
      'agreements',
      'templates',
      'company',
      'serviceCategories',
      'serviceModifiers',
    ]),
    addOnId() {
      return `addon-${this.addon.id}`
    },
    canShowServiceIdInput() {
      return (
        this.hasOrganizationId &&
        this.company.attributes.settings.enable_advanced
      )
    },
    companyEvents() {
      return this.company.attributes.settings.enable_inspection_events
    },
    hasAdvancedEnabled() {
      return this.company.attributes.settings.enable_advanced
    },
    hasAgreements() {
      return this.company.attributes.settings.enable_agreements
    },
    hasOrganizationId() {
      return !!this.company.attributes.organization_id
    },
    hasPayments() {
      return this.company.attributes.settings.enable_payments
    },
    filteredServiceAddOnModifiers() {
      return this.serviceModifiers.filter((modifier) => {
        return (
          modifier.attributes.modifiable_id.toString() ===
            this.addon.id.toString() &&
          modifier.attributes.modifiable_type === 'ServiceAddOn'
        )
      })
    },
    organizationServiceAddOnId: {
      get() {
        return this.addon.attributes.settings.organization_service_add_on_id
      },
      set(value) {
        this.$set(
          this.addon.attributes.settings,
          'organization_service_add_on_id',
          value
        )
      },
    },
  },
  data() {
    return {
      eventsString: '',
    }
  },
  methods: {
    deleteAddOn() {
      this.$store.dispatch('services/deleteAddOn', this.addon.id)
    },
    createServiceAddOnModifier() {
      let serviceModifier = JSON.parse(JSON.stringify(ServiceModifier))
      serviceModifier.attributes.modifiable_id = this.addon.id
      serviceModifier.attributes.modifiable_type = 'ServiceAddOn'
      this.$store.dispatch('services/createServiceModifier', serviceModifier)
    },
  },
  mounted() {
    this.eventsString = this.addon.attributes.events.join(',')
  },
  watch: {
    eventsString(newV, oldV) {
      this.addon.attributes.events = (newV || '')
        .split(',')
        .filter((e) => e.length > 0)
        .map((e) => e.trim())
    },
    addon: {
      handler: debounce(function (newVal) {
        this.$store.dispatch('services/updateServiceAddOn', newVal)
      }, 500),
      deep: true,
    },
  },
  props: ['addon'],
}
</script>

<style scoped>
.addon {
  border-left: 4px solid #9e9e9e;
  margin: 2rem 0;
  padding: 0rem 1rem;
}

.checkbox-top-label {
  position: relative;
}
.checkbox-top-label label {
  top: -1rem;
  width: 300%;
  position: absolute;
  color: rgba(0, 0, 0, 0.54);
}

.v-expansion-panel__container.service-modifiers.v-expansion-panel__container--active
  .v-expansion-panel__header
  .fa-chevron-right {
  transform: rotate(90deg);
}
</style>

<style>
.btn-reorder-add-ons {
  cursor: move;
}
</style>
